import React, { useEffect, useState } from 'react';
import './login.css';
import logo from '../../assets/images/logo.png';

import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

import { useNavigate } from 'react-router-dom';
import ApiServices from '../../services/apiServices';

function Login() {
  const navigate = useNavigate();

  const [loginMode, setLoginMode] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 🔹 Firebase error → Human message
  const getFriendlyError = (err) => {
    const code = err?.code || '';

    switch (code) {
      case 'auth/user-not-found':
        return 'No account found with this email';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again';
      case 'auth/invalid-email':
        return 'Please enter a valid email address';
      case 'auth/user-disabled':
        return 'This account has been disabled';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later';
      case 'auth/popup-closed-by-user':
        return 'Google sign-in was cancelled';
      case 'auth/network-request-failed':
        return 'Network error. Check your internet connection';
      default:
        return 'Something went wrong. Please try again';
    }
  };

  useEffect(() => {
    if (!resendSeconds) return undefined;
    const timer = setInterval(() => {
      setResendSeconds((seconds) => Math.max(seconds - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendSeconds]);

  const resendLabel = () => {
    const minutes = Math.floor(resendSeconds / 60);
    const seconds = String(resendSeconds % 60).padStart(2, '0');
    return `Resend OTP in ${minutes}:${seconds}`;
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      const res = await firebase.auth().signInWithPopup(provider);

      if (res.user) {
        localStorage.setItem('usermail', res.user.email || '');
        localStorage.removeItem('activeProfile');
        localStorage.removeItem('activeProfileId');
        localStorage.setItem('profileSelectionRequired', '1');
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await firebase
        .auth()
        .signInWithEmailAndPassword(email, password);

      if (res.user) {
        localStorage.setItem('usermail', res.user.email || '');
        localStorage.removeItem('activeProfile');
        localStorage.removeItem('activeProfileId');
        localStorage.setItem('profileSelectionRequired', '1');
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await ApiServices.callPostAPI('auth/mobile/send-otp', { mobile, purpose: 'login' });
      if (response.data?.valid) {
        setOtpSent(true);
        setResendSeconds(120);
        return true;
      }
      setError(response.data?.error || 'Unable to send OTP');
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to send OTP');
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleResendOtp = async () => {
    setResending(true);
    await handleSendOtp();
    setResending(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await ApiServices.callPostAPI('auth/mobile/verify-otp', { mobile, otp, purpose: 'login' });
      const customToken = response.data?.data?.customToken;
      if (!response.data?.valid || !customToken) {
        setError(response.data?.error || 'Invalid OTP');
        return;
      }
      const result = await firebase.auth().signInWithCustomToken(customToken);
      if (result.user) {
        localStorage.setItem('usermail', response.data.data.mobile || result.user.uid || '');
        localStorage.removeItem('activeProfile');
        localStorage.removeItem('activeProfileId');
        localStorage.setItem('profileSelectionRequired', '1');
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Mobile login failed. Please try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-main flex justify-content-center">
      <div className="login-main-inner">
        <img src={logo} className="App-logo" alt="logo" />

        <h3>Sign in to your account</h3>

        {error && (
          <p style={{ color: '#ff6b6b', textAlign: 'center', marginBottom: '1rem' }}>
            {error}
          </p>
        )}

        {/* Google Button */}
        <button
          className="button ref"
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          {/* Google Icon */}
          <svg
            className="gooogle"
            viewBox="0 0 48 48"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.68 1.22 9.18 3.6l6.84-6.84C35.82 2.42 30.32 0 24 0 14.64 0 6.6 5.38 2.7 13.22l7.98 6.2C12.7 13.44 17.9 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.1 24.5c0-1.64-.15-3.22-.43-4.75H24v9h12.43c-.54 2.9-2.18 5.36-4.65 7.02l7.19 5.6C43.26 37.5 46.1 31.5 46.1 24.5z"/>
            <path fill="#FBBC05" d="M10.68 28.98c-.48-1.44-.75-2.98-.75-4.48s.27-3.04.75-4.48l-7.98-6.2C.9 17.1 0 20.48 0 24.5s.9 7.4 2.7 10.68l7.98-6.2z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.92-2.14 15.9-5.82l-7.19-5.6c-2 1.34-4.56 2.12-8.71 2.12-6.1 0-11.3-3.94-13.32-9.42l-7.98 6.2C6.6 42.62 14.64 48 24 48z"/>
          </svg>

          Continue with Google
        </button>

        {/* OR Divider */}
        <div className="break-or">
          <span>OR</span>
        </div>

        <div className="login-tabs">
          <button
            type="button"
            className={loginMode === 'email' ? 'active' : ''}
            onClick={() => setLoginMode('email')}
          >
            Email
          </button>
          <button
            type="button"
            className={loginMode === 'mobile' ? 'active' : ''}
            onClick={() => setLoginMode('mobile')}
          >
            Mobile
          </button>
        </div>

        {loginMode === 'email' ? (
          <form onSubmit={handleEmailLogin}>
            <div className="form-group">
              <input
                type="email"
                className="login-input"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                className="login-input"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              {/* Eye Icon */}
              <span
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  color: '#ff03c1',
                  fontSize: '14px'
                }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </span>
            </div>

            <button
              type="submit"
              className="button"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        ) : (
          <form onSubmit={otpSent ? handleVerifyOtp : handleSendOtp}>
            <div className="form-group">
              <input
                type="tel"
                className="login-input"
                placeholder="Mobile number"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                  setOtpSent(false);
                  setResendSeconds(0);
                  setOtp('');
                }}
                required
              />
            </div>
            {otpSent && (
              <div className="form-group">
                <input
                  type="text"
                  className="login-input"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength="6"
                  required
                />
                <button
                  type="button"
                  className="resend-otp"
                  onClick={handleResendOtp}
                  disabled={loading || resending || resendSeconds > 0}
                >
                  {resending ? 'Resending...' : (resendSeconds > 0 ? resendLabel() : 'Resend OTP')}
                </button>
              </div>
            )}
            <button type="submit" className="button" disabled={loading}>
              {loading ? 'Please wait...' : (otpSent ? 'Verify OTP' : 'Send OTP')}
            </button>
          </form>
        )}

        {/* Signup */}
        <div className="code-text">
          Don’t have an account?
<span
  onClick={() => navigate('/signup')}
  style={{ color: '#ff03c1', cursor: 'pointer' }}
>
  Sign up
</span>

        </div>

      </div>
    </div>
  );
}

export default Login;
